import {
  TuningProfile,
  TuningProfileDetail,
  TuningProfilePreview,
  TuningProfileSettingsUpdate,
} from '../../models/TuningProfile.model'
import { TuningProfileDTO, TuningProfileDetailDTO, TuningProfilePreviewDTO } from '../../models/TuningProfileDTO.model'
import FlowfeedApiQueryService from '../FlowfeedApiQueryService/FlowfeedApiQueryService'

export class TuningProfileService {
  /**
   * @description Lists the tuning profiles saved for a trail system, alphabetically
   */
  public async list(trailSystemId: number): Promise<TuningProfile[]> {
    try {
      const profiles = await new FlowfeedApiQueryService().get<TuningProfileDTO[]>({
        endpoint: 'tuning-profile',
        params: { trailSystemId },
      })
      return Array.isArray(profiles) ? profiles.map(TuningProfileService.formatTuningProfile) : []
    } catch (error) {
      console.error(error)
      throw TuningProfileService.errorMessage(error)
    }
  }

  /**
   * @description Saves the trail system's current tuning values as a new profile, which becomes the active one
   */
  public async add(trailSystemId: number, name: string): Promise<TuningProfile> {
    try {
      const profile = await new FlowfeedApiQueryService().post<TuningProfileDTO>({
        endpoint: 'tuning-profile',
        data: { trailSystemId, name },
      })
      return TuningProfileService.formatTuningProfile(profile)
    } catch (error) {
      console.error(error)
      throw TuningProfileService.errorMessage(error)
    }
  }

  public async rename(id: number, name: string): Promise<TuningProfile> {
    try {
      const profile = await new FlowfeedApiQueryService().patch<TuningProfileDTO>({
        endpoint: `tuning-profile/${id}`,
        data: { name },
      })
      return TuningProfileService.formatTuningProfile(profile)
    } catch (error) {
      console.error(error)
      throw TuningProfileService.errorMessage(error)
    }
  }

  public async delete(id: number): Promise<void> {
    try {
      await new FlowfeedApiQueryService().delete({ endpoint: `tuning-profile/${id}` })
    } catch (error) {
      console.error(error)
      throw TuningProfileService.errorMessage(error)
    }
  }

  /**
   * @description A profile's values for the editor: the trail system's, then every trail's, alphabetically
   */
  public async getDetail(id: number): Promise<TuningProfileDetail> {
    try {
      const detail = await new FlowfeedApiQueryService().get<TuningProfileDetailDTO>({
        endpoint: `tuning-profile/${id}`,
      })
      return { ...detail, profile: TuningProfileService.formatTuningProfile(detail.profile) }
    } catch (error) {
      console.error(error)
      throw TuningProfileService.errorMessage(error)
    }
  }

  /**
   * @description Saves the editor's changes. Editing the active profile also updates the live values.
   */
  public async updateSettings(id: number, update: TuningProfileSettingsUpdate): Promise<TuningProfileDetail> {
    try {
      const detail = await new FlowfeedApiQueryService().put<TuningProfileDetailDTO>({
        endpoint: `tuning-profile/${id}/settings`,
        data: update,
      })
      return { ...detail, profile: TuningProfileService.formatTuningProfile(detail.profile) }
    } catch (error) {
      console.error(error)
      throw TuningProfileService.errorMessage(error)
    }
  }

  /**
   * @description What applying the profile would change on the trail system and its trails
   */
  public async preview(id: number): Promise<TuningProfilePreview> {
    try {
      const preview = await new FlowfeedApiQueryService().get<TuningProfilePreviewDTO>({
        endpoint: `tuning-profile/${id}/preview`,
      })
      return { ...preview, profile: TuningProfileService.formatTuningProfile(preview.profile) }
    } catch (error) {
      console.error(error)
      throw TuningProfileService.errorMessage(error)
    }
  }

  /**
   * @description Copies the profile's values onto its trail system and trails and makes it the active profile
   */
  public async apply(id: number): Promise<TuningProfile> {
    try {
      const profile = await new FlowfeedApiQueryService().post<TuningProfileDTO>({
        endpoint: `tuning-profile/${id}/apply`,
      })
      return TuningProfileService.formatTuningProfile(profile)
    } catch (error) {
      console.error(error)
      throw TuningProfileService.errorMessage(error)
    }
  }

  public static formatTuningProfile(profile: TuningProfileDTO): TuningProfile {
    return {
      id: profile.id,
      trailSystemId: profile.trailSystemId,
      name: profile.name,
      active: profile.active,
      trailCount: profile.trailCount,
      updatedAt: new Date(profile.updatedAt),
      createdAt: new Date(profile.createdAt),
    }
  }

  private static errorMessage(error: unknown): string {
    if (error instanceof Error) {
      return error.message
    }
    if ((error as { data?: { message?: string } })?.data?.message) {
      return (error as { data: { message: string } }).data.message
    }
    return 'Sorry, there was a problem with your request.'
  }
}
